import { FC, useMemo } from 'react';
import { RadixTable, TColumn } from '@features/ui';
import { TPayoutsTotalsReportTable } from '@src/types';
import { formatCurrency } from '@lib/utils.ts';
import type { TPayoutsByPackageAnalysisRes } from '@src/types/reports';

interface PayoutsTotalsReportTableProps {
  data?: TPayoutsByPackageAnalysisRes[];
}

// Helper function to map API response to table data format
const mapApiDataToTableFormat = (apiData: TPayoutsByPackageAnalysisRes[]): TPayoutsTotalsReportTable[] => {
  return apiData.map((item) => ({
    packages: item.package,
    totalPaAccounts: item.total_pa_accounts,
    totalPayouts: item.total_payouts,
    payoutUniqueAccounts: item.unique_accounts,
    payoutUniqueUsers: item.unique_users,
    totalPayout: parseFloat(item.total_payout_amount),
    accountsWithPayout1: item.accounts_with_1_payout,
    accountsWithPayout2: item.accounts_with_2_payouts,
    accountsWithPayout3: item.accounts_with_3_payouts,
    accountsWithPayout4: item.accounts_with_4_payouts,
    accountsWithPayout5: item.accounts_with_5_payouts,
    accountsWithPayout6: item.accounts_with_6_payouts,
    accountsWithPayout7: item.accounts_with_7_payouts,
  }));
};

// Helper function to compute totals row
const computeTotalsRow = (data: TPayoutsTotalsReportTable[]): TPayoutsTotalsReportTable | null => {
  if (data.length === 0) return null;

  return {
    packages: 'TOTALS',
    totalPaAccounts: data.reduce((sum, row) => sum + (row.totalPaAccounts || 0), 0),
    totalPayouts: data.reduce((sum, row) => sum + (row.totalPayouts || 0), 0),
    payoutUniqueAccounts: data.reduce((sum, row) => sum + (row.payoutUniqueAccounts || 0), 0),
    payoutUniqueUsers: data.reduce((sum, row) => sum + (row.payoutUniqueUsers || 0), 0),
    totalPayout: data.reduce((sum, row) => sum + (row.totalPayout || 0), 0),
    accountsWithPayout1: data.reduce((sum, row) => sum + (row.accountsWithPayout1 || 0), 0),
    accountsWithPayout2: data.reduce((sum, row) => sum + (row.accountsWithPayout2 || 0), 0),
    accountsWithPayout3: data.reduce((sum, row) => sum + (row.accountsWithPayout3 || 0), 0),
    accountsWithPayout4: data.reduce((sum, row) => sum + (row.accountsWithPayout4 || 0), 0),
    accountsWithPayout5: data.reduce((sum, row) => sum + (row.accountsWithPayout5 || 0), 0),
    accountsWithPayout6: data.reduce((sum, row) => sum + (row.accountsWithPayout6 || 0), 0),
    accountsWithPayout7: data.reduce((sum, row) => sum + (row.accountsWithPayout7 || 0), 0),
  };
};

export const PayoutsTotalsReportTable: FC<PayoutsTotalsReportTableProps> = ({ data = [] }) => {
  const tableData = useMemo(() => {
    const mapped = mapApiDataToTableFormat(data);
    const totals = computeTotalsRow(mapped);
    return totals ? [...mapped, totals] : mapped;
  }, [data]);

  const columns = useMemo(
    (): TColumn<TPayoutsTotalsReportTable>[] => [
      {
        title: 'Package Name',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.packages ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Total PA Accounts`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.totalPaAccounts ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Total Payouts`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.totalPayouts ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Payout Unique Accounts`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.payoutUniqueAccounts ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Payout Unique Users`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.payoutUniqueUsers ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Total Payout $`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              ${formatCurrency(item?.totalPayout ?? 0)}
            </p>
          );
        },
      },
      {
        title: `Accounts with 1 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout1 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 2 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout2 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 3 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout3 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 4 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout4 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 5 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout5 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 6 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout6 ?? '-'}
            </p>
          );
        },
      },
      {
        title: `Accounts with 7 Payout`,
        className: 'text-center',
        render: (item, index) => {
          const isLast = tableData.length === (index ?? 0) + 1;

          return (
            <p className={isLast ? ' font-bold' : ''}>
              {item?.accountsWithPayout7 ?? '-'}
            </p>
          );
        },
      },
    ],
    [tableData]
  );

  return <RadixTable columns={columns} data={tableData} />;
};
